import { isAxiosError, type AxiosError } from 'axios';
import { create } from 'zustand';
import { getHubSpotContext } from '@/lib/hubspotContext';
import { getHubSpotUserName } from '@/lib/hubspotUser';
import { isCustomerFacingPath, setMarkerCustomData } from '@/lib/marker';

/**
 * Foutmeldingen met alles wat nodig is om ze na te speuren. Elke melding krijgt een
 * korte code; de details staan in het foutpaneel en gaan mee met een Marker-melding.
 */

export interface FailedRequest {
  method: string;
  url: string;
  status?: number;
  /** Uit de `X-Request-Id`-header; staat ook in de serverlogs. */
  requestId?: string;
  response?: string;
}

export interface ErrorReport {
  code: string;
  time: Date;
  /** De melding die de gebruiker zag. */
  title: string;
  /** Wat er misging, in één regel. */
  cause: string;
  request?: FailedRequest;
  /** Naam, melding en stack van de fout in de browser. */
  stack?: string;
  page: string;
  user?: string;
  portalId?: string;
  build: string;
  browser: string;
}

const MAX_REPORTS = 20;
const MAX_RESPONSE_LENGTH = 2000;
const MAX_STACK_LENGTH = 4000;
const CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

const STATUS_HINTS: Record<number, string> = {
  400: 'verzoek klopt niet',
  401: 'niet ingelogd',
  403: 'geen toegang',
  404: 'niet gevonden',
  409: 'conflict met een andere wijziging',
  410: 'niet meer beschikbaar',
  413: 'te groot',
  422: 'gegevens ongeldig',
  429: 'te veel verzoeken',
  500: 'interne serverfout',
  502: 'server niet bereikbaar',
  503: 'server tijdelijk niet beschikbaar',
  504: 'server reageerde niet op tijd',
};

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

function createCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return `F-${Array.from(bytes, (byte) => CODE_ALPHABET[byte % CODE_ALPHABET.length]).join('')}`;
}

/** Code die een API-fout omzet in een leesbare melding, geeft de API-fout mee als `cause`. */
function findAxiosError(error: unknown): AxiosError | undefined {
  let current = error;
  for (let depth = 0; current && depth < 5; depth++) {
    if (isAxiosError(current)) return current;
    current = (current as { cause?: unknown }).cause;
  }
  return undefined;
}

function serverMessage(data: unknown): string | undefined {
  if (typeof data === 'string') {
    const text = data.trim();
    // HTML-foutpagina's van een proxy staan volledig onder "Serverantwoord".
    return text && !text.startsWith('<') ? truncate(text, 300) : undefined;
  }
  if (!data || typeof data !== 'object') return undefined;
  const { message, error } = data as Record<string, unknown>;
  return [message, error].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  );
}

function responseText(data: unknown): string | undefined {
  if (data === undefined || data === null || data === '') return undefined;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return `[${data.type || 'bestand'}, ${data.size} bytes]`;
  }
  if (typeof data === 'string') return truncate(data, MAX_RESPONSE_LENGTH);
  try {
    return truncate(JSON.stringify(data, null, 2), MAX_RESPONSE_LENGTH);
  } catch {
    return String(data);
  }
}

function requestUrl(error: AxiosError) {
  const base = error.config?.baseURL ?? '';
  const path = error.config?.url ?? '';
  const url =
    /^https?:\/\//.test(path) || !base
      ? path
      : `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  // De querystring kan een wachtwoord of token bevatten.
  return url.split('?')[0];
}

function describeRequestFailure(
  error: AxiosError,
  readableMessage?: string
): Pick<ErrorReport, 'cause' | 'request'> {
  const response = error.response;
  const requestId = response?.headers?.['x-request-id'];
  const request: FailedRequest = {
    method: (error.config?.method ?? 'get').toUpperCase(),
    url: requestUrl(error),
    status: response?.status,
    requestId: typeof requestId === 'string' && requestId ? requestId : undefined,
    response: responseText(response?.data),
  };

  if (response) {
    const hint = STATUS_HINTS[response.status];
    const message = serverMessage(response.data) ?? readableMessage;
    const status = hint ? `${response.status} (${hint})` : String(response.status);
    return {
      request,
      cause: message ? `Server gaf ${status}: ${message}` : `Server gaf ${status}`,
    };
  }
  if (error.code === 'ERR_CANCELED') return { request, cause: 'Verzoek afgebroken' };
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return { request, cause: 'De server reageerde niet op tijd' };
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { request, cause: 'Geen internetverbinding' };
  }
  return {
    request,
    cause: `Server niet bereikbaar (${error.code ?? 'netwerkfout'}: ${error.message})`,
  };
}

/** Wat er misging, los van waar de fout vandaan kwam. */
export function describeError(error: unknown): Pick<ErrorReport, 'cause' | 'request' | 'stack'> {
  const axiosError = findAxiosError(error);
  if (axiosError) {
    // Een download-antwoord is een Blob; de code eromheen heeft de melding er al uitgehaald.
    const readable =
      error instanceof Error && error.message !== axiosError.message ? error.message : undefined;
    return describeRequestFailure(axiosError, readable);
  }

  if (error instanceof Error) {
    const name = error.name && error.name !== 'Error' ? `${error.name}: ` : '';
    return {
      cause: `${name}${error.message || 'onbekende fout'}`,
      stack: error.stack ? truncate(error.stack, MAX_STACK_LENGTH) : undefined,
    };
  }
  if (typeof error === 'string') return { cause: error || 'onbekende fout' };
  return { cause: responseText(error) ?? 'onbekende fout' };
}

interface ErrorReportState {
  /** Nieuwste eerst. */
  reports: ErrorReport[];
  openCode: string | null;
}

export const useErrorReports = create<ErrorReportState>(() => ({ reports: [], openCode: null }));

const formatTime = (time: Date) => time.toLocaleString('nl-NL');

/** De velden van een melding in vaste volgorde; lege velden vallen weg. */
export function errorReportFields(report: ErrorReport): Array<[label: string, value: string]> {
  const { request } = report;
  const fields: Array<[string, string | undefined]> = [
    ['Code', report.code],
    ['Tijdstip', formatTime(report.time)],
    ['Oorzaak', report.cause],
    ['Verzoek', request && `${request.method} ${request.url}`],
    ['HTTP-status', request?.status?.toString()],
    ['Request-ID', request?.requestId],
    ['Pagina', report.page],
    ['Gebruiker', report.user],
    ['Portal', report.portalId],
    ['Versie', report.build],
  ];
  return fields.filter((field): field is [string, string] => Boolean(field[1]));
}

/** Platte tekst om te plakken in een chat of ticket. */
export function formatErrorReport(report: ErrorReport): string {
  return [
    `Foutmelding: ${report.title}`,
    ...errorReportFields(report).map(([label, value]) => `${label}: ${value}`),
    `Browser: ${report.browser}`,
    report.request?.response && `\nServerantwoord:\n${report.request.response}`,
    report.stack && `\nTechnische details:\n${report.stack}`,
  ]
    .filter(Boolean)
    .join('\n');
}

function markerData(report: ErrorReport, earlier: ErrorReport[]): Record<string, string> {
  const data: Record<string, string> = { Foutmelding: report.title };
  for (const [label, value] of errorReportFields(report)) data[label] = value;
  if (report.request?.response) data.Serverantwoord = report.request.response;
  if (report.stack) data['Technische details'] = report.stack;
  const others = earlier.filter((other) => other !== report).slice(0, 5);
  if (others.length) {
    data['Eerdere fouten'] = others
      .map((other) => `${other.code} ${formatTime(other.time)} ${other.title}: ${other.cause}`)
      .join('\n');
  }
  return data;
}

function currentPortalId() {
  try {
    return getHubSpotContext().portalId || undefined;
  } catch {
    return undefined;
  }
}

/** Legt een fout vast; de nieuwste melding gaat mee met elke Marker-melding. */
export function reportError(title: string, error: unknown, componentStack?: string): ErrorReport {
  const described = describeError(error);
  const report: ErrorReport = {
    code: createCode(),
    time: new Date(),
    title,
    ...described,
    stack:
      [described.stack, componentStack && `Componenten:${componentStack}`]
        .filter(Boolean)
        .join('\n\n') || undefined,
    page: window.location.pathname,
    user: getHubSpotUserName() ?? undefined,
    portalId: currentPortalId(),
    build: import.meta.env.VITE_APP_BUILD || 'lokaal',
    browser: navigator.userAgent,
  };
  const reports = [report, ...useErrorReports.getState().reports].slice(0, MAX_REPORTS);
  useErrorReports.setState({ reports });
  setMarkerCustomData(markerData(report, reports));
  console.error(`[${report.code}] ${title}`, error);
  return report;
}

/** Foutmelding op de pagina zelf. Zonder technische oorzaak, of op een klantpagina, geen `report`. */
export interface InlineError {
  message: string;
  report?: ErrorReport;
}

export function inlineError(message: string, error?: unknown): InlineError {
  if (error === undefined || isCustomerFacingPath()) return { message };
  return { message, report: reportError(message, error) };
}

export function openErrorReport(code: string) {
  const { reports } = useErrorReports.getState();
  const report = reports.find((candidate) => candidate.code === code);
  if (!report) return;
  // Een melding via Marker gaat over de fout die nu open staat.
  setMarkerCustomData(markerData(report, reports));
  useErrorReports.setState({ openCode: code });
}

export function closeErrorReport() {
  useErrorReports.setState({ openCode: null });
}
